import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { localDateStr } from '../utils/date'
import { COMPANY } from '../constants/company'
import { fetchClients, upsertClient } from '../utils/clients'
import useIsMobile from '../hooks/useIsMobile'

const INK = '#1a1916'
const MIN_ROWS = 18 // 인쇄 시 빈 줄 포함 최소 행 수

const emptyItem = () => ({ name:'', unit:'', qty:'', due:'', note:'' })

/* ——— 흑백 발주서 양식 공용 스타일 ——— */
const bd = `1px solid ${INK}`
const bdBold = `2px solid ${INK}`
const thS = { border:bd, padding:'0 1mm', fontSize:'8.5pt', fontWeight:700, color:INK, textAlign:'center', whiteSpace:'nowrap', background:'#f4f3f0', lineHeight:1.2 }
const tdS = { border:bd, padding:'0 1.5mm', fontSize:'9pt', color:INK, lineHeight:1.2 }
const lblS = { ...thS, width:'20mm', fontSize:'7.5pt' }
const cellInput = {
  width:'100%', border:'none', outline:'none', background:'transparent',
  fontFamily:'inherit', fontSize:'inherit', fontWeight:'inherit', color:'inherit',
  textAlign:'inherit', padding:0, boxSizing:'border-box',
}

function fmtKoreanDate(dateStr) {
  if (!dateStr) return ''
  const [y, m, d] = dateStr.split('-')
  return `${y}년 ${m}월 ${d}일`
}

export default function Order({ profile }) {
  const [list, setList] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [showDetail, setShowDetail] = useState(null)
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(false)
  const [clients, setClients] = useState([])
  const isMobile = useIsMobile()

  useEffect(() => { fetchList(); fetchClients().then(setClients) }, [])

  async function fetchList() {
    const { data, error } = await supabase.from('purchase_orders').select('*').order('created_at', { ascending: false })
    if (error) console.error('purchase_orders 조회:', error.message)
    if (data) setList(data)
  }

  const padItems = arr => {
    const items = arr.map(it => ({ ...it }))
    while (items.length < MIN_ROWS) items.push(emptyItem())
    return items
  }

  function openNewModal() {
    setEditingId(null)
    setForm({
      order_no: '',
      order_date: localDateStr(),
      vendor_name: '',
      biz_type: COMPANY.bizType,
      biz_item: COMPANY.bizItem,
      memo: '',
      items: padItems([]),
    })
    setShowModal(true)
  }

  function openEditModal(po) {
    setEditingId(po.id)
    setForm({
      order_no: po.order_no || '',
      order_date: po.order_date,
      vendor_name: po.vendor_name || '',
      biz_type: po.biz_type || COMPANY.bizType,
      biz_item: po.biz_item || COMPANY.bizItem,
      memo: po.memo || '',
      items: padItems(Array.isArray(po.items) ? po.items : []),
    })
    setShowDetail(null)
    setShowModal(true)
  }

  function setItem(idx, patch) {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }))
  }

  function addItemRow() {
    setForm(f => ({ ...f, items: [...f.items, emptyItem()] }))
  }

  function clearItemRow(idx) {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? emptyItem() : it) }))
  }

  async function submitOrder() {
    if (!form.vendor_name) { alert('받는 업체명을 입력하세요'); return }
    const validItems = form.items.filter(it => it.name || it.unit || String(it.qty).trim() || it.due || it.note)
    if (!validItems.length) { alert('품명을 1개 이상 입력하세요'); return }
    setLoading(true)
    const payload = {
      order_no: form.order_no,
      order_date: form.order_date || localDateStr(),
      vendor_name: form.vendor_name,
      biz_type: form.biz_type,
      biz_item: form.biz_item,
      memo: form.memo,
      items: validItems,
      created_by: profile.id,
      created_by_name: profile.name,
    }
    const q = editingId
      ? supabase.from('purchase_orders').update(payload).eq('id', editingId)
      : supabase.from('purchase_orders').insert(payload)
    const { error } = await q
    if (error) {
      alert('저장 실패: ' + error.message + '\n\nSupabase에 purchase_orders 테이블이 있는지 확인하세요 (supabase/sql/purchase_orders.sql 실행)')
      setLoading(false)
      return
    }
    // 새 거래처면 자동 등록 (실패해도 무시)
    await upsertClient({ name: form.vendor_name })
    fetchClients().then(setClients)
    setShowModal(false)
    fetchList()
    setLoading(false)
  }

  async function deleteOrder(id) {
    if (!window.confirm('이 발주서를 삭제할까요?')) return
    const { error } = await supabase.from('purchase_orders').delete().eq('id', id)
    if (error) { alert('삭제 실패: ' + error.message); return }
    setShowDetail(null)
    fetchList()
  }

  const filtered = list.filter(po => {
    const q = search.toLowerCase()
    if (!q) return true
    return po.vendor_name?.toLowerCase().includes(q) || po.order_no?.toLowerCase().includes(q) || po.order_date?.includes(q)
  })

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>발주서</div>
          <div style={{ fontSize:13, color:'#a09e99' }}>작성 · 저장 · 인쇄 (발주처: {COMPANY.name})</div>
        </div>
        <button onClick={openNewModal}
          style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
          + 발주서 작성
        </button>
      </div>

      {/* 검색 */}
      <div style={{ marginBottom:20 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="업체명, 번호, 날짜 검색..."
          style={inputStyle} />
      </div>

      {/* 모바일: 카드 / 데스크톱: 테이블 */}
      {isMobile ? (
        filtered.length === 0 ? (
          <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, textAlign:'center', padding:40, color:'#a09e99', fontSize:13 }}>
            작성된 발주서가 없습니다
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.map(po => (
              <div key={po.id} onClick={() => setShowDetail(po)}
                style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:14, cursor:'pointer', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, gap:8 }}>
                  <span style={{ fontSize:14, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{po.vendor_name} 귀하</span>
                  <span style={{ fontSize:11, color:'#a09e99', flexShrink:0 }}>{po.order_date}</span>
                </div>
                <div style={{ fontSize:12, color:'#6b6860' }}>
                  {po.order_no && <span style={{ marginRight:8 }}>No. {po.order_no}</span>}
                  {po.items?.[0]?.name}{po.items?.length > 1 ? ` 외 ${po.items.length - 1}건` : ''}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
      <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {['일자','No.','받는 업체','품명','작성자',''].map((h, i) => (
                <th key={i} style={{ padding:'10px 14px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign:'center', padding:40, color:'#a09e99' }}>작성된 발주서가 없습니다</td></tr>
            ) : filtered.map(po => (
              <tr key={po.id} style={{ borderBottom:'1px solid #e5e3de' }}>
                <td style={{ padding:'12px 14px', whiteSpace:'nowrap' }}>{po.order_date}</td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860' }}>{po.order_no || '—'}</td>
                <td style={{ padding:'12px 14px', fontWeight:600 }}>{po.vendor_name}</td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {po.items?.[0]?.name}{po.items?.length > 1 ? ` 외 ${po.items.length - 1}건` : ''}
                </td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860' }}>{po.created_by_name}</td>
                <td style={{ padding:'12px 14px' }}>
                  <button onClick={() => setShowDetail(po)}
                    style={{ padding:'4px 12px', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, whiteSpace:'nowrap' }}>
                    보기/인쇄
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* 작성/수정 모달 — 양식 그대로 편집 */}
      {showModal && form && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:820, maxWidth:'96vw', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>{editingId ? '발주서 수정' : '발주서 작성'}</span>
              <button onClick={() => setShowModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:16, overflowX:'auto' }}>
              {/* 거래처 불러오기 */}
              {clients.length > 0 && (
                <div style={{ maxWidth:'197mm', margin:'0 auto 10px' }}>
                  <select value="" onChange={e => {
                    const c = clients.find(x => x.id === e.target.value)
                    if (c) setForm(f => ({ ...f, vendor_name: c.name }))
                  }}
                    style={{ padding:'8px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', cursor:'pointer', maxWidth:'100%' }}>
                    <option value="">📇 거래처 불러오기 (선택 시 자동 입력)</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ width:'197mm', margin:'0 auto', fontFamily:'Noto Sans KR, sans-serif', background:'#fff' }}>
                {/* No. + 제목 */}
                <div style={{ display:'flex', alignItems:'flex-end', marginBottom:'2mm' }}>
                  <div style={{ width:'50mm', fontSize:'9pt', display:'flex', alignItems:'baseline', gap:'1mm' }}>
                    No.
                    <input value={form.order_no} onChange={e => setForm({...form, order_no:e.target.value})}
                      placeholder="번호"
                      style={{ ...cellInput, borderBottom:bd, fontSize:'9pt', width:'34mm', textAlign:'center' }} />
                  </div>
                  <div style={{ flex:1, textAlign:'center', fontSize:'18pt', fontWeight:800, letterSpacing:'0.5em' }}>발 주 서</div>
                  <div style={{ width:'50mm' }} />
                </div>

                {/* 발주처 정보 블록 */}
                <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, tableLayout:'fixed' }}>
                  <tbody>
                    <tr style={{ height:'9mm' }}>
                      <td rowSpan={5} style={{ ...tdS, width:'7mm', textAlign:'center', fontWeight:700, fontSize:'9pt', padding:'0 1mm' }}>
                        발<br/><br/>주<br/><br/>처
                      </td>
                      <td style={lblS}>사 업 자<br/>등록번호</td>
                      <td colSpan={3} style={{ ...tdS, textAlign:'center', fontWeight:800, fontSize:'13pt', letterSpacing:'0.15em' }}>
                        {COMPANY.regNo}
                      </td>
                      <td rowSpan={5} style={{ ...tdS, width:'62mm', padding:0, verticalAlign:'top' }}>
                        <div style={{ padding:'1.5mm 2mm', borderBottom:bd, fontSize:'8.5pt', textAlign:'right' }}>
                          일자 : {fmtKoreanDate(form.order_date)}
                          <input type="date" value={form.order_date} onChange={e => setForm({...form, order_date:e.target.value})}
                            style={{ display:'block', width:'100%', border:'none', outline:'none', background:'#f9f8f6', fontSize:'8pt', fontFamily:'inherit', marginTop:'1mm', textAlign:'right' }} />
                        </div>
                        <div style={{ padding:'4mm 2mm 2mm', textAlign:'center', fontSize:'11pt', fontWeight:700, display:'flex', alignItems:'baseline', justifyContent:'center', gap:'1.5mm' }}>
                          <input value={form.vendor_name} onChange={e => setForm({...form, vendor_name:e.target.value})}
                            placeholder="받는 업체명 *"
                            style={{ ...cellInput, borderBottom:bd, width:'34mm', textAlign:'center', fontSize:'11pt', fontWeight:700 }} />
                          <span>귀하</span>
                        </div>
                        <div style={{ padding:'3mm 2mm', textAlign:'center', fontSize:'9pt' }}>
                          아래와 같이 발주합니다.
                        </div>
                      </td>
                    </tr>
                    <tr style={{ height:'8mm' }}>
                      <td style={lblS}>상 호</td>
                      <td style={{ ...tdS, width:'42mm', textAlign:'center', fontWeight:700 }}>{COMPANY.name}</td>
                      <td style={{ ...lblS, width:'12mm' }}>성<br/>명</td>
                      <td style={{ ...tdS, textAlign:'center', fontWeight:600 }}>{COMPANY.ceo} <span style={{ fontSize:'7.5pt', color:'#6b6860' }}>(인)</span></td>
                    </tr>
                    <tr style={{ height:'8mm' }}>
                      <td style={lblS}>사 업 장<br/>소 재 지</td>
                      <td colSpan={3} style={{ ...tdS, fontSize:'8.5pt' }}>{COMPANY.addr}</td>
                    </tr>
                    <tr style={{ height:'8mm' }}>
                      <td style={lblS}>업 태</td>
                      <td style={{ ...tdS, textAlign:'center' }}>
                        <input value={form.biz_type} onChange={e => setForm({...form, biz_type:e.target.value})} style={cellInput} />
                      </td>
                      <td style={{ ...lblS, width:'12mm' }}>종<br/>목</td>
                      <td style={{ ...tdS, textAlign:'center' }}>
                        <input value={form.biz_item} onChange={e => setForm({...form, biz_item:e.target.value})} style={cellInput} />
                      </td>
                    </tr>
                    <tr style={{ height:'8mm' }}>
                      <td style={lblS}>연 락 처</td>
                      <td colSpan={3} style={{ ...tdS, fontSize:'8.5pt' }}>
                        Tel. {COMPANY.tel}, Fax. {COMPANY.fax}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* 품목 테이블 */}
                <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
                  <thead>
                    <tr style={{ height:'7mm' }}>
                      <th style={{ ...thS, width:'10mm' }}>순번</th>
                      <th style={thS}>품 명 / 규 격</th>
                      <th style={{ ...thS, width:'16mm' }}>단 위</th>
                      <th style={{ ...thS, width:'16mm' }}>수 량</th>
                      <th style={{ ...thS, width:'30mm' }}>희망납품일자</th>
                      <th style={{ ...thS, width:'30mm' }}>비 고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const hasContent = it.name || it.unit || String(it.qty).trim() || it.due || it.note
                      const cellTd = { ...tdS, fontSize:'8.5pt' }
                      // 순번: 내용 있는 줄까지의 채워진 줄 수
                      const seq = hasContent ? form.items.slice(0, i + 1).filter(x => x.name || x.unit || String(x.qty).trim() || x.due || x.note).length : ''
                      return (
                        <tr key={i} style={{ height:'7mm' }}>
                          <td style={{ ...cellTd, textAlign:'center', color:'#6b6860' }}>{seq}</td>
                          <td style={cellTd}><input value={it.name} onChange={e => setItem(i, { name:e.target.value })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'center' }}><input value={it.unit} onChange={e => setItem(i, { unit:e.target.value })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'center' }}><input value={it.qty} onChange={e => setItem(i, { qty:e.target.value })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'center' }}><input value={it.due} onChange={e => setItem(i, { due:e.target.value })} placeholder="" style={cellInput} /></td>
                          <td style={{ ...cellTd, position:'relative' }}>
                            <input value={it.note} onChange={e => setItem(i, { note:e.target.value })} style={cellInput} />
                            {hasContent && (
                              <button type="button" onClick={() => clearItemRow(i)} title="줄 비우기"
                                style={{ position:'absolute', right:1, top:'50%', transform:'translateY(-50%)', border:'none', background:'transparent', color:'#b83030', cursor:'pointer', fontSize:10, padding:'0 2px', lineHeight:1 }}>✕</button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {/* 하단 비고 박스 */}
                <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
                  <tbody>
                    <tr style={{ height:'22mm' }}>
                      <td style={{ ...tdS, verticalAlign:'top', padding:'1.5mm 2mm' }}>
                        <textarea value={form.memo} onChange={e => setForm({...form, memo:e.target.value})}
                          placeholder="비고 / 전달사항"
                          style={{ ...cellInput, height:'100%', resize:'none', fontSize:'8.5pt', lineHeight:1.5 }} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', maxWidth:'197mm', margin:'10px auto 0' }}>
                <span style={{ fontSize:11, color:'#a09e99' }}>칸을 클릭해서 바로 입력 · 순번은 자동</span>
                <button type="button" onClick={addItemRow}
                  style={{ padding:'4px 12px', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, flexShrink:0 }}>
                  + 줄 추가
                </button>
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={submitOrder} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '저장 중...' : (editingId ? '수정하기' : '저장하기')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 보기/인쇄 모달 */}
      {showDetail && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowDetail(null)}>
          <style>{`
            @page { size: A4 portrait; margin: 8mm; }
            @media print {
              body * { visibility: hidden !important; }
              .order-print, .order-print * { visibility: visible !important; }
              .order-print { position: fixed !important; left: 0; top: 0; margin: 0 !important; padding: 0 !important; }
            }
          `}</style>
          <div style={{ background:'#fff', borderRadius:14, width:880, maxWidth:'96vw', maxHeight:'92vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:14, fontWeight:700 }}>{showDetail.order_date} · {showDetail.vendor_name} 귀하</span>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => openEditModal(showDetail)}
                  style={{ padding:'6px 14px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:12 }}>✏️ 수정</button>
                <button onClick={() => deleteOrder(showDetail.id)}
                  style={{ padding:'6px 14px', border:'none', borderRadius:8, background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:12 }}>🗑️ 삭제</button>
                <button onClick={() => window.print()}
                  style={{ padding:'6px 14px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:600 }}>🖨️ 인쇄</button>
                <button onClick={() => setShowDetail(null)}
                  style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
              </div>
            </div>
            <div style={{ padding:16, overflowX:'auto' }}>
              <OrderPrintSheet po={showDetail} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** 발주서 인쇄 양식 — A4 세로 1장 */
function OrderPrintSheet({ po }) {
  const items = Array.isArray(po.items) ? po.items : []
  const rows = [...items]
  while (rows.length < MIN_ROWS) rows.push(null)

  return (
    <div className="order-print" style={{ width:'197mm', margin:'0 auto', fontFamily:'Noto Sans KR, sans-serif', background:'#fff' }}>
      {/* No. + 제목 */}
      <div style={{ display:'flex', alignItems:'flex-end', marginBottom:'2mm' }}>
        <div style={{ width:'50mm', fontSize:'9pt' }}>
          No. <span style={{ borderBottom:bd, padding:'0 3mm', display:'inline-block', minWidth:'28mm', textAlign:'center' }}>{po.order_no}</span>
        </div>
        <div style={{ flex:1, textAlign:'center', fontSize:'18pt', fontWeight:800, letterSpacing:'0.5em' }}>발 주 서</div>
        <div style={{ width:'50mm' }} />
      </div>

      {/* 발주처 정보 블록 */}
      <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, tableLayout:'fixed' }}>
        <tbody>
          <tr style={{ height:'9mm' }}>
            <td rowSpan={5} style={{ ...tdS, width:'7mm', textAlign:'center', fontWeight:700, fontSize:'9pt', padding:'0 1mm' }}>
              발<br/><br/>주<br/><br/>처
            </td>
            <td style={lblS}>사 업 자<br/>등록번호</td>
            <td colSpan={3} style={{ ...tdS, textAlign:'center', fontWeight:800, fontSize:'13pt', letterSpacing:'0.15em' }}>
              {COMPANY.regNo}
            </td>
            <td rowSpan={5} style={{ ...tdS, width:'62mm', padding:0, verticalAlign:'top' }}>
              <div style={{ padding:'1.5mm 2mm', borderBottom:bd, fontSize:'8.5pt', textAlign:'right' }}>
                일자 : {fmtKoreanDate(po.order_date)}
              </div>
              <div style={{ padding:'5mm 2mm 2mm', textAlign:'center', fontSize:'12pt', fontWeight:700 }}>
                {po.vendor_name} 귀하
              </div>
              <div style={{ padding:'3mm 2mm', textAlign:'center', fontSize:'9pt' }}>
                아래와 같이 발주합니다.
              </div>
            </td>
          </tr>
          <tr style={{ height:'8mm' }}>
            <td style={lblS}>상 호</td>
            <td style={{ ...tdS, width:'42mm', textAlign:'center', fontWeight:700 }}>{COMPANY.name}</td>
            <td style={{ ...lblS, width:'12mm' }}>성<br/>명</td>
            <td style={{ ...tdS, textAlign:'center', fontWeight:600 }}>{COMPANY.ceo} <span style={{ fontSize:'7.5pt', color:'#6b6860' }}>(인)</span></td>
          </tr>
          <tr style={{ height:'8mm' }}>
            <td style={lblS}>사 업 장<br/>소 재 지</td>
            <td colSpan={3} style={{ ...tdS, fontSize:'8.5pt' }}>{COMPANY.addr}</td>
          </tr>
          <tr style={{ height:'8mm' }}>
            <td style={lblS}>업 태</td>
            <td style={{ ...tdS, textAlign:'center' }}>{po.biz_type}</td>
            <td style={{ ...lblS, width:'12mm' }}>종<br/>목</td>
            <td style={{ ...tdS, textAlign:'center' }}>{po.biz_item}</td>
          </tr>
          <tr style={{ height:'8mm' }}>
            <td style={lblS}>연 락 처</td>
            <td colSpan={3} style={{ ...tdS, fontSize:'8.5pt' }}>
              Tel. {COMPANY.tel}, Fax. {COMPANY.fax}
            </td>
          </tr>
        </tbody>
      </table>

      {/* 품목 테이블 */}
      <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
        <thead>
          <tr style={{ height:'7mm' }}>
            <th style={{ ...thS, width:'10mm' }}>순번</th>
            <th style={thS}>품 명 / 규 격</th>
            <th style={{ ...thS, width:'16mm' }}>단 위</th>
            <th style={{ ...thS, width:'16mm' }}>수 량</th>
            <th style={{ ...thS, width:'30mm' }}>희망납품일자</th>
            <th style={{ ...thS, width:'30mm' }}>비 고</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((it, i) => (
            <tr key={i} style={{ height:'7mm' }}>
              <td style={{ ...tdS, textAlign:'center', fontSize:'8.5pt', color:'#6b6860' }}>{it ? i + 1 : ''}</td>
              <td style={{ ...tdS, fontSize:'8.5pt', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>{it?.name}</td>
              <td style={{ ...tdS, textAlign:'center', fontSize:'8.5pt' }}>{it?.unit}</td>
              <td style={{ ...tdS, textAlign:'center', fontSize:'8.5pt' }}>{it?.qty}</td>
              <td style={{ ...tdS, textAlign:'center', fontSize:'8.5pt' }}>{it?.due}</td>
              <td style={{ ...tdS, fontSize:'8.5pt' }}>{it?.note}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 하단 비고 박스 */}
      <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
        <tbody>
          <tr style={{ height:'22mm' }}>
            <td style={{ ...tdS, verticalAlign:'top', padding:'1.5mm 2mm', fontSize:'8.5pt', lineHeight:1.5, whiteSpace:'pre-wrap' }}>
              {po.memo}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
